import { useEffect, type ReactNode } from 'react'

import { Meter } from '../../components/ui.tsx'
import { assessPassword, type PasswordVerdict } from '../../../shared/auth.ts'

/** The narrow centred column every auth screen uses. */
export function AuthCard({ children, width = 400 }: { children: ReactNode; width?: number }) {
  return <div style={{ maxWidth: width, margin: '24px auto 0' }}>{children}</div>
}

export function AuthHeading({ title, sub }: { title: string; sub?: ReactNode }) {
  useEffect(() => {
    document.title = `${title} — KellyStake`
  }, [title])

  return (
    <div className="u-center" style={{ marginBottom: 18 }}>
      <h1 style={{ fontSize: 'var(--fs-2xl)' }}>{title}</h1>
      {sub && (
        <p className="u-muted u-sm" style={{ marginTop: 6 }}>
          {sub}
        </p>
      )}
    </div>
  )
}

/**
 * A link inside a consent label.
 *
 * Two things it must not do, both of which the obvious `<Link>` does: toggle the very
 * consent it sits inside (a click anywhere in a `<label>` activates its control), and
 * navigate away from a half-completed form. So it stops propagation and opens in a new
 * tab — you cannot be made to agree to something by trying to read it.
 */
export function LegalLink({ children }: { children: ReactNode }) {
  return (
    <a href="/legal" target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
      {children}
    </a>
  )
}

/**
 * A real checkbox underneath, so it is keyboard- and screen-reader-operable; the design
 * system's `.checkbox` styling on top.
 */
export function Consent({
  checked,
  onChange,
  children,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  children: ReactNode
}) {
  return (
    <label className={`checkbox${checked ? ' checked' : ''}`}>
      <input type="checkbox" className="u-sr" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span className="box" aria-hidden="true">
        ✓
      </span>
      <span className="u-sm">{children}</span>
    </label>
  )
}

/**
 * The three agreements every account makes, however it was created.
 *
 * One component rather than two copies, because these are a legal requirement and a
 * requirement that drifts between the password form and the Google form is one that is
 * only half met.
 */
export interface Consents {
  confirmAge: boolean
  acceptTerms: boolean
  readRisk: boolean
}

export const NO_CONSENTS: Consents = { confirmAge: false, acceptTerms: false, readRisk: false }

export function allConsented(c: Consents): boolean {
  return c.confirmAge && c.acceptTerms && c.readRisk
}

export function ConsentBlock({
  value,
  onChange,
}: {
  value: Consents
  onChange: (next: Consents) => void
}) {
  return (
    <div className="stack sm">
      <Consent checked={value.confirmAge} onChange={(v) => onChange({ ...value, confirmAge: v })}>
        I confirm I am 18 years of age or older
      </Consent>
      <Consent checked={value.acceptTerms} onChange={(v) => onChange({ ...value, acceptTerms: v })}>
        I accept the <LegalLink>Terms of use</LegalLink> and <LegalLink>Privacy policy</LegalLink>
      </Consent>
      <Consent checked={value.readRisk} onChange={(v) => onChange({ ...value, readRisk: v })}>
        I have read the <LegalLink>risk disclosure</LegalLink> and understand I can lose money
      </Consent>
    </div>
  )
}

/**
 * The strength meter under a new password.
 *
 * It scores with `shared/auth.ts` — the same module the server enforces with — so it can
 * never say "strong" about something the server is about to reject. The label is always
 * present in text, because the meter's colour alone must not be the signal.
 */
export function PasswordStrength({ password, email }: { password: string; email?: string }) {
  if (!password) return null
  const verdict: PasswordVerdict = assessPassword(password, email ?? '')
  const tone = verdict.acceptable ? (verdict.score >= 75 ? 'pos' : 'warn') : 'neg'

  return (
    <>
      <div style={{ marginTop: 8 }}>
        <Meter pct={verdict.score} tone={tone} />
      </div>
      <span className={`help${verdict.acceptable ? '' : ' warn'}`} role="status">
        <b>{verdict.label}</b> — {verdict.hint}
      </span>
    </>
  )
}
